import { SEARCH_SHOWCASE_HEIGHT } from 'src/components/deferredMount'
import { DeferredShowcases } from 'src/components/deferredShowcases'
import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Select,
  Text,
  VStack,
} from '@gazin/ecommerce-ui'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction } from 'react'
import { BsGrid1X2Fill } from 'react-icons/bs'
import { FaBars } from 'react-icons/fa'
import Carregando from 'src/components/carregando'
import { PaginationComponent } from 'src/components/Pagination'
import { ProductVerticalWithAnalytics } from 'src/components/productVertical'
import { mapProductToAnalyticsProduct } from 'src/components/productVertical/utils'
import { ProductHorizontalWithAnalytics } from 'src/components/produtctHorizontal/product-horizontal-with-analytics'
import { useProductShowcases } from 'src/contexto/hooks/useProductShowcases'
import { ShowcaseType } from 'src/contexto/types'
import { Showcase } from 'src/services/searchProducts/dtos/related-products-response'
import {
  Product,
  Sort,
  Suggestion,
} from 'src/services/searchProducts/dtos/search-products-response'
import { afterNextPaint } from 'src/util/afterNextPaint'
import { makePageUrl } from 'src/util/url'
import { calculateProductsForPage } from '../utils'

const ProductSectionSearch = dynamic(
  () =>
    import('src/components/ProductSectionSearch').then(
      (module) => module.ProductSectionSearch
    ),
  { ssr: false }
)

// Mesmo corte do layout: abaixo de `lg` é a experiência mobile. Os blocos
// exclusivos saem por CSS para o SSR ser idêntico em qualquer dispositivo.
const MOBILE_ONLY = { base: 'flex', lg: 'none' }
const DESKTOP_ONLY = { base: 'none', lg: 'flex' }

export type ProductListProps = {
  alterarOrdenacao: (value: string) => void
  configPaginas: {
    currentPage: number
    firstPage: number
    lastPage: number
    nextPage: number
    prevPage: number
  }
  isCategoryPage?: boolean
  isFetchingMore?: boolean
  isGrid: boolean
  itemsPerPage: number
  mapProductToProductVertical: (product: Product) => any
  pagina: number
  parametros: { ordenacao: string }
  produtos?: Product[]
  resultsSize?: number
  setIsGrid: Dispatch<SetStateAction<boolean>>
  sort?: Sort[]
  suggestedSearches?: Suggestion[]
}

export const ProductList = ({
  alterarOrdenacao,
  configPaginas,
  isCategoryPage,
  isFetchingMore = false,
  isGrid,
  itemsPerPage,
  mapProductToProductVertical,
  pagina,
  parametros,
  produtos,
  resultsSize,
  setIsGrid,
  sort,
  suggestedSearches,
}: ProductListProps) => {
  const route = useRouter()
  const [categorySlug, subcategorySlug] = Array.isArray(route.query.categoria)
    ? route.query.categoria
    : []
  const indiceFinal =
    resultsSize && Math.min(pagina * itemsPerPage, resultsSize)

  const { showcaseTop, showcaseMiddle, showcaseBottom } = useProductShowcases(
    isCategoryPage ? ShowcaseType.CATEGORY : ShowcaseType.SEARCH,
    isCategoryPage ? { categorySlug, subcategorySlug } : undefined
  )

  function handleClickSeeMore() {
    afterNextPaint(() =>
      route.push(
        {
          pathname: route.pathname,
          query: { ...route.query, pagina: configPaginas.nextPage },
        },
        undefined,
        { shallow: true }
      )
    )
  }

  return (
    <Box width="100%">
      <Flex flexDir="column">
        {showcaseTop?.length > 0 &&
          showcaseTop?.map((showcase: Showcase) => (
            <ProductSectionSearch
              key={showcase.id}
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0].recommendations}
            />
          ))}
      </Flex>

      {route.query.busca && (
        <HStack spacing={2} display={DESKTOP_ONLY}>
          <Text fontSize="1.5rem" fontWeight={600} color="#363843">
            Você pesquisou por ‘
            <Text fontSize="1.5rem" fontWeight={600} color="#0D71F0" as="span">
              {`${route.query.busca?.[0]}`}
            </Text>
            ’
          </Text>
        </HStack>
      )}

      <HStack
        display={DESKTOP_ONLY}
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <Text fontSize="0.875rem" color="#7C8096">
          {resultsSize &&
            calculateProductsForPage(pagina, itemsPerPage, resultsSize)}
        </Text>

        <Flex padding="0.75rem 0rem 2rem 0.375rem" justifyContent="flex-end">
          <Flex alignItems="center">
            <Text
              as="span"
              color="#646981"
              fontSize="0.875rem"
              marginRight="0.875rem"
              width="50%"
            >
              Ordenar por:
            </Text>
            <Select
              borderRadius=".25rem"
              variant="outline"
              onChange={(e) => alterarOrdenacao(e.target.value)}
              value={
                (route.query.sortBy as string) ??
                sort?.find((s) => s.selected)?.name ??
                parametros.ordenacao
              }
            >
              {sort?.map((s) => (
                <option key={s.name} value={s.name}>
                  {s.label}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
      </HStack>

      <HStack
        display={MOBILE_ONLY}
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        height="2rem"
        marginY="0.5rem"
      >
        <Text fontSize="0.875rem" fontWeight={700} as="span" color="#7C8096">
          {resultsSize}{' '}
          <Text as="span" fontWeight={400} fontSize="0.875rem" color="#7C8096">
            resultados encontrados
          </Text>
        </Text>
        <HStack>
          <BsGrid1X2Fill
            color="#0D71F0"
            size={20}
            opacity={!isGrid ? 0.3 : 1}
            onClick={() => setIsGrid(true)}
          />
          <FaBars
            color="#0D71F0"
            size={22}
            style={{ marginLeft: '.625rem' }}
            opacity={isGrid ? 0.3 : 1}
            onClick={() => setIsGrid(false)}
          />
        </HStack>
      </HStack>

      {isGrid ? (
        <Grid
          gap={{
            base: '1.25rem 0.1875rem',
            lg: '1.5rem',
            xl: '1rem',
            '2xl': '2rem',
          }}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, minmax(8.9375rem, 1fr))',
            md: 'repeat(auto-fill, minmax(14rem, 1fr))',
            lg: 'repeat(2, 1fr)',
            xl: 'repeat(3, 1fr)',
            '2xl': 'repeat(4, 1fr)',
          }}
          overflow="hidden"
          width="100%"
          paddingBottom={{ lg: '16px' }}
        >
          {produtos?.map((product) => (
            <ProductVerticalWithAnalytics
              key={product.id}
              product={mapProductToProductVertical(product)}
              analyticsProduct={mapProductToAnalyticsProduct(product)}
            />
          ))}
        </Grid>
      ) : (
        <Box display="grid" gap="1rem 0.1875rem" overflow="hidden" width="100%">
          {produtos?.map((product) => (
            <ProductHorizontalWithAnalytics
              key={product.id}
              product={mapProductToProductVertical(product)}
              analyticsProduct={mapProductToAnalyticsProduct(product)}
            />
          ))}
        </Box>
      )}

      <Flex display={DESKTOP_ONLY} justifyContent="center" width="100%">
        <PaginationComponent pagination={configPaginas} />
      </Flex>

      <VStack display={MOBILE_ONLY} marginY="1.5rem" spacing={2}>
        <Text color="#7C8096" fontSize="0.875rem" as="span">
          {indiceFinal} de {resultsSize} resultados encontrados
        </Text>
        {pagina < configPaginas.lastPage && (
          <>
            <Link
              href={`${makePageUrl(configPaginas.nextPage, route)}`}
              shallow={true}
              hidden
            />
            {isFetchingMore ? (
              <Carregando />
            ) : (
              <Button
                bgColor="#0D71F0"
                color="white"
                borderRadius=".25rem"
                width="100%"
                _hover={{ bgColor: 'none' }}
                onClick={handleClickSeeMore}
              >
                Ver mais
              </Button>
            )}
          </>
        )}
      </VStack>

      <Flex flexDir="column" mt={{ lg: 8 }}>
        <DeferredShowcases
          showcases={showcaseMiddle}
          reservedHeight={SEARCH_SHOWCASE_HEIGHT}
        >
          {(showcase) => (
            <ProductSectionSearch
              title={showcase.name}
              subtitle={showcase.title}
              products={showcase.displays[0].recommendations}
            />
          )}
        </DeferredShowcases>

        <DeferredShowcases
          showcases={showcaseBottom}
          reservedHeight={SEARCH_SHOWCASE_HEIGHT}
        >
          {(showcase) => (
            <ProductSectionSearch
              title={showcase.title}
              subtitle={showcase.subtitle}
              products={showcase.displays[0].recommendations}
            />
          )}
        </DeferredShowcases>
      </Flex>

      {!!suggestedSearches?.length && (
        <Box
          marginTop={{ base: '1.5rem', lg: '3rem' }}
          marginBottom={{ base: '1.5rem', lg: 0 }}
        >
          <Text color="#646981" fontWeight={600} as="span" fontSize="1rem">
            Pesquisas rápidas
          </Text>
          <Flex
            justifyContent="flex-start"
            flexWrap={{ base: 'nowrap', lg: 'wrap' }}
            overflowX={{ base: 'scroll', lg: 'visible' }}
            sx={{ '::-webkit-scrollbar': { display: 'none' } }}
            width="100%"
          >
            {suggestedSearches.map((quer, index) => (
              <Box
                key={index}
                border=".0625rem solid #7C8096"
                paddingX=".75rem"
                paddingY=".5rem"
                borderRadius=".25rem"
                marginRight="1rem"
                marginTop="1rem"
                color="#7C8096"
                fontSize=".875rem"
                height={{ lg: '40px' }}
                width={{ base: '100%', lg: 'auto' }}
              >
                <Text
                  as="span"
                  color="#7C8096"
                  cursor="pointer"
                  whiteSpace={{ base: 'nowrap', lg: 'normal' }}
                  width={{ base: '100%', lg: 'auto' }}
                  onClick={() => {
                    route.push({
                      pathname: '/busca/[...busca]',
                      query: { busca: quer.query },
                    })
                  }}
                >
                  {quer.query}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  )
}
